/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect } from 'react';

import { cssMap, jsx, type StrictXCSSProp } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { FeatureFlagOverrides } from '../feature-flags/feature-flag-overrides';
import type { FeatureFlagList } from '../feature-flags/types';
import { useFeatureFlagOverrides } from '../feature-flags/use-feature-flag-overrides';

import { useExampleFeatureFlags } from './example-feature-flag-context';
import { FINESSE_EXAMPLE_THEME_ATTRIBUTE, loadFinesseExampleThemeStyles } from './finesse-theme';
import type { Background } from './types';

const FINESSE_FEATURE_FLAG = 'platform-dst-tokens-finesse';

const styles = cssMap({
	checkered: {
		backgroundPosition: '0 0, 10px 0, 10px -10px, 0px 10px',
		backgroundSize: '20px 20px',
		backgroundColor: token('elevation.surface'),
		backgroundImage: `
    linear-gradient(
      45deg,
      ${token('elevation.surface.sunken')} 25%,
      transparent 25%
    ),
    linear-gradient(
      135deg,
      ${token('elevation.surface.sunken')} 25%,
      transparent 25%
    ),
    linear-gradient(
      45deg,
      transparent 75%,
      ${token('elevation.surface.sunken')} 75%
    ),
    linear-gradient(
      135deg,
      transparent 75%,
      ${token('elevation.surface.sunken')} 75%
    )`,
	},
	white: {
		backgroundColor: token('elevation.surface'),
	},
	blue: {
		backgroundColor: token('color.background.brand.bold'),
	},
	neutral: {
		backgroundColor: token('color.background.neutral.hovered'),
	},
});

type ExampleBackgroundProps = {
	background?: Background;
	children?: React.ReactNode;
	xcss?: StrictXCSSProp<
		| 'borderBlockEnd'
		| 'borderTopLeftRadius'
		| 'borderTopRightRadius'
		| 'borderBottomLeftRadius'
		| 'borderBottomRightRadius'
		| 'borderStartStartRadius'
		| 'borderStartEndRadius'
		| 'borderEndStartRadius'
		| 'borderEndEndRadius'
		| 'borderRadius',
		never
	>;
	testId?: string;
};

/**
 * __Example background__
 *
 * An example background contains code examples,
 * providing a background, feature flag container,
 * sub tree theming, and customization controls.
 *
 */
const ExampleBackground: ({
	background,
	children,
	xcss,
	testId,
}: ExampleBackgroundProps) => JSX.Element = ({
	background = 'checkered',
	children,
	xcss,
	testId,
}: ExampleBackgroundProps) => {
	const exampleFlags = useExampleFeatureFlags();
	const isFinesseThemeEnabled = exampleFlags?.switcherOverrides[FINESSE_FEATURE_FLAG] === true;

	useEffect(() => {
		if (isFinesseThemeEnabled) {
			void loadFinesseExampleThemeStyles();
		}
	}, [isFinesseThemeEnabled]);

	// Inherit any overrides set by an ancestor <FeatureFlagOverrides> (e.g. the page-level/layout
	// override that enables labelling visuals site-wide). Because <FeatureFlagOverrides> replaces
	// rather than merges the override context, we must spread the parent overrides in here first so
	// examples don't silently fall back to the provider default for flags they don't declare.
	const parentOverrides = useFeatureFlagOverrides();

	// Single system: inherited page/layout overrides, then per-example defaults (featureFlagDefaults)
	// + user toggles (featureFlagSwitchers). Later spreads win. Add static overrides here (e.g. navx
	// for docs) as needed.
	const flagOverrides: Partial<FeatureFlagList> = {
		// Turning on `useIsFhsEnabled` so examples can demonstrate features behind the hook,
		// e.g. the panel splitter tooltip with keyboard shortcuts, without enabling for the entire app.
		'navx-full-height-sidebar': true,
		...parentOverrides,
		...(exampleFlags !== null ? (exampleFlags.defaultOverrides as Partial<FeatureFlagList>) : {}),
		...(exampleFlags !== null ? (exampleFlags.switcherOverrides as Partial<FeatureFlagList>) : {}),
	};

	return (
		<div
			role="group"
			{...{
				[FINESSE_EXAMPLE_THEME_ATTRIBUTE]: isFinesseThemeEnabled ? 'true' : undefined,
			}}
			css={[
				background === 'checkered' && styles.checkered,
				background === 'white' && styles.white,
				background === 'blue' && styles.blue,
				background === 'neutral' && styles.neutral,
			]}
			className={xcss}
			data-testid={testId}
		>
			<FeatureFlagOverrides flagOverrides={flagOverrides}>{children}</FeatureFlagOverrides>
		</div>
	);
};

export default ExampleBackground;
